import type { ColumnMenuOption } from '@labrav/react-components'
import type { Dispatch } from 'redux'

import { InvitationStatus, Role } from '../../../utils/roles'
import { getRoleName } from '../../../utils/getRoleName'
import {
  InviteUserRequestSuccess,
  RequestFailureMessage,
} from '../../../utils/messagesContants'
import { errorLogger as newrelicErrLogger } from '../../../utils/ErrorHandler'
import { updateAppAlert } from '../../../modules/appAlert/actions'
import { getUserProfile } from '../../../modules/userProfile/action'
import { PartnerType } from '../../../types/partner'
import { AuthProvider } from '../../../modules/userProfile/userProfileTypes'
import type { UserState } from '../../../utils/hooks/useGetUsers'
import {
  postResendInvite,
  resendInvitationIdentityService,
} from '../../../api/users'
import {
  getUsers,
  startUserLoading,
  stopUserLoading,
  updateUser,
} from '../../../modules/userList/actions'
import { getAccount } from '../../../modules/partner/getAccount'

export const partitionInvitedUsers = (invitedUsers: UserState[]) => {
  const ownerUsers = (invitedUsers || []).filter(
    userItem => userItem.roles[0] === Role.PARTNER_OWNER
  )
  const otherUsers = (invitedUsers || [])
    .filter(userItem => userItem.roles[0] !== Role.PARTNER_OWNER)
    .sort(function (a, b) {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  return { ownerUsers, otherUsers }
}

export const getRoleChangeSuccessMsg = (
  selectedUser: UserState,
  fromRole?: Role
) => {
  if (!fromRole) {
    return ''
  }
  return `Role of (${selectedUser.email}) changed to ${getRoleName(
    selectedUser.roles[0]
  )} from ${getRoleName(fromRole)}`
}

export const getInvitedUserMenuOptions = (userItem: UserState) => [
  {
    key:
      getRoleName(userItem.roles[0]) === 'Admin'
        ? 'admin-to-staff'
        : 'staff-to-admin',
    label:
      getRoleName(userItem.roles[0]) === 'Admin' ? 'Make staff' : 'Make admin',
  },
  ...(userItem.invitationStatus === InvitationStatus.INVITED
    ? [
        {
          key: 'resend-invite',
          label: 'Resend invite',
        },
      ]
    : []),
]

export const isInvitedUserActionDisabled = (
  isStaffUser: boolean | undefined,
  roleName: string
) => !!isStaffUser || roleName === 'Owner'

type Auth0Identity = {
  partner_id?: string
  partner_user_id?: string
}

export const refreshSignedInUser = async ({
  dispatch,
  auth0Enabled,
  user,
}: {
  dispatch: Dispatch
  auth0Enabled: boolean
  user?: Auth0Identity
}) => {
  if (auth0Enabled && user?.partner_id) {
    try {
      dispatch(getAccount(user.partner_id as string, PartnerType.User) as never)
      dispatch(
        getUserProfile(user.partner_id, user.partner_user_id as string) as never
      )
    } catch (err: unknown) {
      newrelicErrLogger(err as Error)
    }
  }
}

export const resendInvitedUserInvite = async ({
  dispatch,
  partnerId,
  auth0Enabled,
  selectedUser,
  handleClose,
}: {
  dispatch: Dispatch
  partnerId?: string
  auth0Enabled: boolean
  selectedUser: UserState
  handleClose?: () => void
}) => {
  if (partnerId) {
    try {
      await dispatch(startUserLoading())
      const resendVerificationEmail = auth0Enabled
        ? resendInvitationIdentityService
        : postResendInvite
      const response = await resendVerificationEmail(partnerId, selectedUser.id)
      if (response.status) {
        dispatch(
          updateAppAlert({
            message: InviteUserRequestSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
            sidePanelAlert: true,
          })
        )
      }
    } catch (error: unknown) {
      const errMess =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || RequestFailureMessage
      dispatch(
        updateAppAlert({
          message: errMess,
          messageType: 'ERROR',
          autoClose: true,
          sidePanelAlert: true,
        })
      )
      newrelicErrLogger(error as Error, {
        message: error,
      })
    } finally {
      await dispatch(stopUserLoading())
      handleClose && handleClose()
    }
  }
}

export const changeInvitedUserRole = async ({
  dispatch,
  partnerId,
  auth0Enabled,
  isAdminUser,
  userProfileId,
  selectedUser,
  handleClose,
  fromRole,
  refreshUser,
}: {
  dispatch: Dispatch
  partnerId?: string
  auth0Enabled: boolean
  isAdminUser: boolean | undefined
  userProfileId?: string
  selectedUser: UserState
  handleClose?: () => void
  fromRole?: Role
  refreshUser: () => void
}) => {
  if (partnerId) {
    const adminToStaff =
      isAdminUser &&
      selectedUser.id === userProfileId &&
      selectedUser.roles[0] === Role.PARTNER_STAFF
    const successMsg = getRoleChangeSuccessMsg(selectedUser, fromRole)
    const response = await dispatch(
      updateUser(
        partnerId,
        selectedUser.id,
        { ...selectedUser },
        auth0Enabled ? AuthProvider.AUTH0 : AuthProvider.OKTA,
        successMsg
      ) as never
    )
    if (response && (response as { success?: boolean }).success) {
      handleClose && handleClose()
      if (adminToStaff) {
        refreshUser()
      }
      await dispatch(
        getUsers(
          {
            partnerId: partnerId || '',
            pageNumber: 1,
            pageSize: 500,
            authProvider: auth0Enabled ? AuthProvider.AUTH0 : AuthProvider.OKTA,
          },
          PartnerType.User
        ) as never
      )
    }
  }
}

export const selectInvitedUserMenuOption = ({
  option,
  userItem,
  handleClose,
  handleResendInvite,
  changeRole,
}: {
  option: ColumnMenuOption
  userItem: UserState
  handleClose?: () => void
  handleResendInvite?: (user: UserState, handleClose?: () => void) => void
  changeRole?: (
    user: UserState,
    handleClose?: () => void,
    fromRole?: Role
  ) => void
}) => {
  if (option.key === 'resend-invite') {
    handleResendInvite && handleResendInvite(userItem, handleClose)
  } else if (option.key === 'admin-to-staff') {
    changeRole &&
      changeRole(
        {
          ...userItem,
          roles: [Role.PARTNER_STAFF],
        },
        handleClose,
        userItem.roles[0]
      )
  } else if (option.key === 'staff-to-admin') {
    changeRole &&
      changeRole(
        {
          ...userItem,
          roles: [Role.PARTNER_ADMIN],
        },
        handleClose,
        userItem.roles[0]
      )
  }
}
